import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Locations from './pages/Locations';
import Attendance from './pages/Attendance';
import AttendanceSummary from './pages/AttendanceSummary';
import Kiosk from './pages/Kiosk';
import Layout from './components/Layout';
import LeaveManagement from './pages/LeaveManagement';
import Reports from './pages/Reports';
import Payroll from './pages/Payroll';
import MyDashboard from './pages/MyDashboard';
import BulkImport from './pages/BulkImport';
import Analytics from './pages/Analytics';
import PayslipUpload from './pages/PayslipUpload';
import EmployeeProfile from './pages/EmployeeProfile';
import TaskMaster from './pages/TaskMaster';
import TaskAssignment from './pages/TaskAssignment';
import MyTasks from './pages/MyTasks';

// Use current origin for API calls (works on any domain)
const BACKEND_URL = window.location.origin;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// API instance
export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Force logout deactivated employees using existing tokens
    if (error.response?.status === 403 && error.response?.data?.detail?.includes('deactivated')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (employeeId, password) => {
    try {
      const response = await api.post('/auth/login', {
        employee_id: employeeId,
        password: password,
      });
      
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', userData.token);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }, []);

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-soft text-primary text-lg font-medium">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/my-dashboard" replace />;
  }

  return children;
}

function RoleBasedRedirect() {
  const { user } = useAuth();
  if (user?.role === 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to="/my-dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleBasedRedirect />} />
        <Route path="dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
        <Route path="my-dashboard" element={<MyDashboard />} />
        <Route
          path="employees"
          element={
            <ProtectedRoute adminOnly>
              <Employees />
            </ProtectedRoute>
          }
        />
        <Route
          path="bulk-import"
          element={
            <ProtectedRoute adminOnly>
              <BulkImport />
            </ProtectedRoute>
          }
        />
        <Route
          path="locations"
          element={
            <ProtectedRoute adminOnly>
              <Locations />
            </ProtectedRoute>
          }
        />
        <Route path="attendance" element={<Attendance />} />
        <Route path="summary" element={<AttendanceSummary />} />
        <Route path="leaves" element={<LeaveManagement />} />
        <Route
          path="reports"
          element={
            <ProtectedRoute adminOnly>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="payroll"
          element={
            <ProtectedRoute adminOnly>
              <Payroll />
            </ProtectedRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <ProtectedRoute adminOnly>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="payslip-upload"
          element={
            <ProtectedRoute adminOnly>
              <PayslipUpload />
            </ProtectedRoute>
          }
        />
        <Route
          path="employee/:employeeId"
          element={
            <ProtectedRoute adminOnly>
              <EmployeeProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="task-master"
          element={
            <ProtectedRoute adminOnly>
              <TaskMaster />
            </ProtectedRoute>
          }
        />
        <Route
          path="task-assignment"
          element={
            <ProtectedRoute adminOnly>
              <TaskAssignment />
            </ProtectedRoute>
          }
        />
        <Route path="my-tasks" element={<MyTasks />} />
      </Route>
      <Route path="*" element={<RoleBasedRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
